import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import type { User, ThemeClasses } from "../types";
import useIsMonitor from "../../../../hooks/useIsMonitor";
import { useDisableCRUD } from "../../../../store/admin/companiesStore";
import { CRUDTooltip } from "../../../../components/base/CRUDTooltip";
interface UserGridCardProps {
  user: User;
  themeClasses: ThemeClasses;
  isDarkMode: boolean;
  onDetails: (user: User) => void;
  onEdit: (user: User) => void;
  onDelete?: (user: User) => void;
  onHardDelete?: (user: User) => void;
  onActivate?: (user: User) => void;
  onChangePassword?: (user: User) => void;
  isAdminReseller?: boolean;
  isSelectionMode?: boolean;
  isSelected?: boolean;
  onToggleSelection?: (user: User) => void;
}

export function UserGridCard({
  user,
  themeClasses,
  isDarkMode,
  onDetails,
  onEdit,
  onDelete,
  onHardDelete,
  onActivate,
  onChangePassword,
  isAdminReseller,
  isSelectionMode = false,
  isSelected = false,
  onToggleSelection,
}: UserGridCardProps) {
  const { t } = useTranslation("adminSections");
  const [isWideScreen, setIsWideScreen] = useState(false);
  const { isMonitor, isReadOnly } = useIsMonitor();
  const disableCRUD = useDisableCRUD();
  useEffect(() => {
    const checkScreenSize = () => {
      setIsWideScreen(window.innerWidth > 1600);
    };

    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);

    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  const getStatusColor = (status: string): string => {
    switch (status) {
      case "R5tW0uX4yY9cF6dH2aI8":
        return "text-primary-500 bg-primary-500/10";
      case "S6uX1vY5zZ0dG7eI3bJ9":
        return "text-red-500 bg-red-500/10";
      default:
        return "text-red-500 bg-red-500/10";
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case "R5tW0uX4yY9cF6dH2aI8":
        return t("users.status.active");
      case "S6uX1vY5zZ0dG7eI3bJ9":
        return t("users.status.inactive");
      default:
        return t("users.status.inactive");
    }
  };

  const getInitials = (firstName: string, lastName: string) => {
    return `${firstName?.charAt(0) || ""}${lastName?.charAt(0) || ""}`.toUpperCase();
  };

  const handleCardClick = () => {
    if (isSelectionMode && onToggleSelection) {
      onToggleSelection(user);
    }
  };

  return (
    <div
      className={`${themeClasses.bgInput} border ${themeClasses.cardBorder} rounded-xl p-5 hover:shadow-lg transition-all ${
        isSelectionMode ? "cursor-pointer" : ""
      } ${isSelected ? "ring-2 ring-primary-500" : ""}`}
      onClick={isSelectionMode ? handleCardClick : undefined}
    >
      {/* Selection Checkbox */}
      {isSelectionMode && (
        <div className="flex items-center mb-3">
          <div
            className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
              isSelected
                ? "bg-primary-500 border-primary-500"
                : isDarkMode
                  ? "border-gray-500"
                  : "border-gray-300"
            }`}
          >
            {isSelected && <i className="ri-check-line text-white text-sm"></i>}
          </div>
          <span className={`ml-2 text-sm ${themeClasses.textSecondary}`}>
            {isSelected ? t("users.selected") : t("users.selectToAction")}
          </span>
        </div>
      )}

      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div
            className={`w-12 h-12 rounded-xl flex items-center justify-center ${
              isDarkMode ? "bg-dark-border" : "bg-gray-100"
            }`}
          >
            <span className={`text-lg font-semibold ${themeClasses.text}`}>
              {getInitials(user.first_name, user.last_name)}
            </span>
          </div>
          <div>
            <h3 className={`font-semibold ${themeClasses.text} line-clamp-1`}>
              {user.first_name} {user.last_name}
            </h3>
            <p className={`text-sm ${themeClasses.textSecondary} line-clamp-1`}>
              @{user.username || t("users.fields.noUsername")}
            </p>
          </div>
        </div>
        <span
          className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(
            user.status_id,
          )}`}
        >
          {getStatusLabel(user.status_id)}
        </span>
      </div>

      {/* Info */}
      <div className="space-y-2 mb-4">
        <div className="flex items-center space-x-2">
          <i className={`ri-mail-line ${themeClasses.textSecondary}`}></i>
          <span className={`text-sm ${themeClasses.textSecondary} truncate`}>
            {user.email}
          </span>
        </div>
        {user.phone && (
          <div className="flex items-center space-x-2">
            <i className={`ri-phone-line ${themeClasses.textSecondary}`}></i>
            <span className={`text-sm ${themeClasses.textSecondary}`}>
              {user.phone}
            </span>
          </div>
        )}
        {user.company && (
          <div className="flex items-center space-x-2">
            <i className={`ri-building-line ${themeClasses.textSecondary}`}></i>
            <span className={`text-sm ${themeClasses.textSecondary} truncate`}>
              {user.company.name}
            </span>
          </div>
        )}
        {user.role && (
          <div className="flex items-center space-x-2">
            <i
              className={`ri-shield-user-line ${themeClasses.textSecondary}`}
            ></i>
            <span
              className={`text-sm ${themeClasses.textSecondary} truncate capitalize`}
            >
              {user.role.name}
            </span>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className={`pt-3 border-t ${themeClasses.cardBorder}`}>
        {user.status_id === "R5tW0uX4yY9cF6dH2aI8" ? (
          // Layout para usuarios activos
          isWideScreen ? (
            // Pantallas anchas (>1600px): botones horizontales
            <div className="w-full flex gap-1 mb-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onDetails(user);
                }}
                className="flex-1 py-1.5 px-2 bg-primary-500 hover:bg-primary-400 text-white rounded-md text-sm font-medium transition-colors flex items-center justify-center gap-1"
              >
                <i className="ri-eye-line text-base"></i>
                <span>{t("users.actions.details")}</span>
              </button>
              {!isMonitor && !isReadOnly && (
                <CRUDTooltip className="flex-1">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onEdit(user);
                    }}
                    disabled={disableCRUD}
                    className={`w-full py-1.5 px-2 ${themeClasses.buttonHover} border ${themeClasses.cardBorder} rounded-md ${themeClasses.buttonText} ${themeClasses.buttonTextHover} text-sm font-medium transition-colors flex items-center justify-center gap-1`}
                  >
                    <i className="ri-pencil-line text-base"></i>
                    <span>{t("users.actions.edit")}</span>
                  </button>
                </CRUDTooltip>
              )}
              {/* Desactivar - solo para admin_reseller */}
              {isAdminReseller && onDelete && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(user);
                  }}
                  disabled={isSelectionMode || disableCRUD}
                  className={`flex-1 py-1.5 px-2 bg-orange-50 hover:bg-orange-100 text-orange-600 rounded-md text-sm font-medium transition-colors flex items-center justify-center gap-1 ${isSelectionMode || disableCRUD ? "opacity-50 cursor-not-allowed" : ""}`}
                  title={t("users.actions.deactivate")}
                >
                  <i className="ri-close-circle-line text-base"></i>
                  <span>{t("users.actions.deactivate")}</span>
                </button>
              )}
              {/* Cambiar contraseña - solo para admin_reseller */}
              {isAdminReseller && onChangePassword && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onChangePassword(user);
                  }}
                  disabled={isSelectionMode || disableCRUD}
                  className={`flex-1 py-1.5 px-2 bg-blue-50 hover:bg-blue-100 text-blue-600 rounded-md text-sm font-medium transition-colors flex items-center justify-center gap-1 ${isSelectionMode || disableCRUD ? "opacity-50 cursor-not-allowed" : ""}`}
                  title={t("users.actions.changePassword")}
                >
                  <i className="ri-lock-line text-base"></i>
                  <span>{t("users.actions.changePasswordShort")}</span>
                </button>
              )}
            </div>
          ) : (
            // Pantallas pequeñas (≤1600px): Detalles arriba, Editar + Desactivar abajo
            <div className="w-full flex flex-col gap-2 mb-2">
              {/* Botón Detalles arriba */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onDetails(user);
                }}
                className="w-full py-1.5 px-2 bg-primary-500 hover:bg-primary-400 text-gray-900 rounded-md text-sm font-medium transition-colors flex items-center justify-center gap-1"
              >
                <i className="ri-eye-line text-base"></i>
                <span>{t("users.actions.details")}</span>
              </button>

              {/* Editar y Desactivar abajo */}
              <div className="flex gap-2">
                {!isMonitor && !isReadOnly && (
                  <CRUDTooltip className="flex-1">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onEdit(user);
                      }}
                      disabled={disableCRUD}
                      className={`w-full py-1.5 px-1 ${themeClasses.buttonHover} border ${themeClasses.cardBorder} rounded-md ${themeClasses.buttonText} ${themeClasses.buttonTextHover} text-sm font-medium transition-colors flex items-center justify-center gap-0.5`}
                    >
                      <i className="ri-pencil-line text-base"></i>
                      <span>{t("users.actions.edit")}</span>
                    </button>
                  </CRUDTooltip>
                )}
                {/* Desactivar - solo para admin_reseller */}
                {isAdminReseller && onDelete && !isMonitor && !isReadOnly && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete(user);
                    }}
                    disabled={isSelectionMode || disableCRUD}
                    className={`flex-1 py-1.5 px-1 bg-orange-50 hover:bg-orange-100 text-orange-600 rounded-md text-sm font-medium transition-colors flex items-center justify-center gap-0.5 ${isSelectionMode || disableCRUD ? "opacity-50 cursor-not-allowed" : ""}`}
                    title={t("users.actions.deactivate")}
                  >
                    <i className="ri-close-circle-line text-base"></i>
                    <span>{t("users.actions.deactivate")}</span>
                  </button>
                )}
                {/* Cambiar contraseña - solo para admin_reseller */}
                {isAdminReseller && onChangePassword && !isMonitor && !isReadOnly && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onChangePassword(user);
                    }}
                    disabled={isSelectionMode || disableCRUD}
                    className={`flex-1 py-1.5 px-1 bg-blue-50 hover:bg-blue-100 text-blue-600 rounded-md text-sm font-medium transition-colors flex items-center justify-center gap-0.5 ${isSelectionMode || disableCRUD ? "opacity-50 cursor-not-allowed" : ""}`}
                    title={t("users.actions.changePassword")}
                  >
                    <i className="ri-lock-line text-base"></i>
                    <span>{t("users.actions.changePasswordShort")}</span>
                  </button>
                )}
              </div>
            </div>
          )
        ) : (
          // Layout para usuarios inactivos: Detalles arriba, Editar y Eliminar abajo
          <div className="w-full flex flex-col gap-2 mb-2">
            {/* Botón Detalles arriba */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDetails(user);
              }}
              className="w-full py-1.5 px-2 bg-primary-500 hover:bg-primary-400 text-gray-900 rounded-md text-sm font-medium transition-colors flex items-center justify-center gap-1"
            >
              <i className="ri-eye-line text-base"></i>
              <span>{t("users.actions.details")}</span>
            </button>

            {/* Activar, Editar y Eliminar abajo */}
            <div className="flex gap-2">
              {/* Activar - solo para admin_reseller */}
              {isAdminReseller && onActivate && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onActivate(user);
                  }}
                  disabled={isSelectionMode || disableCRUD}
                  className={`flex-1 py-1.5 px-1 bg-green-50 hover:bg-green-100 text-green-600 rounded-md text-sm font-medium transition-colors flex items-center justify-center gap-0.5 ${isSelectionMode || disableCRUD ? "opacity-50 cursor-not-allowed" : ""}`}
                  title={t("users.actions.activate")}
                >
                  <i className="ri-check-line text-base"></i>
                  <span>{t("users.actions.activate")}</span>
                </button>
              )}
              {!isMonitor && !isReadOnly && (
                <CRUDTooltip className="flex-1">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onEdit(user);
                    }}
                    disabled={disableCRUD}
                    className={`w-full py-1.5 px-1 ${themeClasses.buttonHover} border ${themeClasses.cardBorder} rounded-md ${themeClasses.buttonText} ${themeClasses.buttonTextHover} text-sm font-medium transition-colors flex items-center justify-center gap-0.5`}
                  >
                    <i className="ri-pencil-line text-base"></i>
                    <span>{t("users.actions.edit")}</span>
                  </button>
                </CRUDTooltip>
              )}
              {/* Eliminar - solo para admin_reseller */}
              {isAdminReseller && onHardDelete && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onHardDelete(user);
                  }}
                  disabled={isSelectionMode || disableCRUD}
                  className={`flex-1 py-1.5 px-1 bg-red-50 hover:bg-red-100 text-red-600 rounded-md text-sm font-medium transition-colors flex items-center justify-center gap-0.5 ${isSelectionMode || disableCRUD ? "opacity-50 cursor-not-allowed" : ""}`}
                  title={t("users.actions.deletePermanently")}
                >
                  <i className="ri-delete-bin-line text-base"></i>
                  <span>{t("users.actions.delete")}</span>
                </button>
              )}
              {/* Cambiar contraseña - solo para admin_reseller */}
              {isAdminReseller && onChangePassword && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onChangePassword(user);
                  }}
                  disabled={isSelectionMode || disableCRUD}
                  className={`flex-1 py-1.5 px-1 bg-blue-50 hover:bg-blue-100 text-blue-600 rounded-md text-sm font-medium transition-colors flex items-center justify-center gap-0.5 ${isSelectionMode || disableCRUD ? "opacity-50 cursor-not-allowed" : ""}`}
                  title={t("users.actions.changePassword")}
                >
                  <i className="ri-lock-line text-base"></i>
                  <span>{t("users.actions.changePasswordShort")}</span>
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
